import imageCompression from 'browser-image-compression';

/** Hard limit — must match backend Vacasa AI model limit (guest-content-validation). */
export const MAX_GUEST_CONTENT_IMAGE_BYTES = 2_000_000;

const MAX_DIMENSION = 2048;
const MAX_ATTEMPTS = 4;
/** Aim under the hard limit so library rounding cannot slip past it. */
const TARGET_RATIO = 0.95;

/**
 * Compress an image file to at most MAX_GUEST_CONTENT_IMAGE_BYTES as JPEG.
 * Retries with more aggressive settings if the first pass still exceeds the limit.
 * HEIC/PNG/WebP are re-encoded when the browser can decode them.
 */
export async function compressImageToJpeg(file: File, maxBytes = MAX_GUEST_CONTENT_IMAGE_BYTES): Promise<File> {
    if (!file.type.startsWith('image/') && !isLikelyImage(file)) {
        throw new Error('Unsupported file type');
    }

    // Already within the AI limit and JPEG — skip work on slow phones.
    if (file.type === 'image/jpeg' && file.size <= maxBytes) {
        return file;
    }

    let maxWidthOrHeight = MAX_DIMENSION;
    let initialQuality = 0.85;
    let maxSizeMB = (maxBytes * TARGET_RATIO) / (1024 * 1024);
    let compressed: Blob = file;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        compressed = await imageCompression(file, {
            maxSizeMB,
            maxWidthOrHeight,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality,
            maxIteration: 15,
        });

        if (compressed.size <= maxBytes) {
            break;
        }

        // Still too large — tighten target, dimensions, and quality for the next pass.
        maxSizeMB *= 0.85;
        maxWidthOrHeight = Math.max(640, Math.round(maxWidthOrHeight * 0.75));
        initialQuality = Math.max(0.4, initialQuality - 0.15);
    }

    // Hard guarantee: never return a file the AI validation lambda would reject.
    if (compressed.size > maxBytes) {
        throw new Error(`Image exceeds the ${maxBytes} byte limit after compression`);
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([compressed], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
    });
}

function isLikelyImage(file: File): boolean {
    return /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}
